/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * ArceeDefaultFooter — the platform's built-in footer.
 *
 * Rendered whenever a Page Builder page enables its footer in "default" mode
 * (page_config.footer.mode === 'default'), i.e. the page author wants a footer
 * but has not designed one. Both marks are links:
 *   arcee.io logo/wordmark → https://arcee.io
 *   RUDRACORESOFT logo     → https://rudracoresoft.com
 *
 * The markup is ported from components/DynamicFooter.js, which carried this
 * block as a permanent attribution on the older homepage footer.
 */

import React from 'react';
import VelLogo from '../VelLogo';

const ARCEE_URL = 'https://arcee.io';
const RUDRA_URL = 'https://rudracoresoft.com';

const RudraLogo = ({ color }) => (
  <svg width="140" height="32" viewBox="0 0 280 54" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2"  width="60" height="14" rx="5" fill={color} fillOpacity="0.8" />
    <rect x="2" y="20" width="60" height="14" rx="5" fill={color} fillOpacity="0.8" />
    <rect x="2" y="38" width="60" height="14" rx="5" fill={color} fillOpacity="0.8" />
    <circle cx="32" cy="27" r="6" fill="#ff811a" />
    <text x="75" y="30" fill={color} fillOpacity="0.9" fontFamily="Arial, sans-serif" fontWeight="100" fontSize="37">RUDRA</text>
    <text x="75" y="52" fill={color} fillOpacity="0.9" fontFamily="Arial, sans-serif" fontWeight="100" fontSize="24">CORESOFT</text>
  </svg>
);

// Relative luminance test so the marks stay legible on a dark background.
function isDarkBg(bgColor) {
  if (!bgColor || !/^#[0-9a-f]{6}$/i.test(bgColor)) return false;
  const r = parseInt(bgColor.slice(1, 3), 16);
  const g = parseInt(bgColor.slice(3, 5), 16);
  const b = parseInt(bgColor.slice(5, 7), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 < 128;
}

export default function ArceeDefaultFooter({ theme, bgColor = '#fafafa' }) {
  const dark        = isDarkBg(bgColor);
  const accentColor = dark ? '#e2e8f0' : (theme?.primary || '#4f46e5');
  const mutedColor  = dark ? '#94a3b8' : '#888';

  return (
    <footer style={{ background: bgColor, fontSize: '0.9rem' }}>
      <div className="container">
        <div style={{
          borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}>

          {/* Powered by arcee.io */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, color: mutedColor, fontSize: '0.85rem' }}>
            Powered by
            <a
              href={ARCEE_URL}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <VelLogo size={24} />
              <span style={{ fontWeight: 800, fontSize: '1rem' }}>
                <span style={{ color: accentColor }}>arcee</span>
                <span style={{ color: (dark ? accentColor : theme?.secondary) || accentColor }}>.io</span>
              </span>
            </a>
          </div>

          {/* Developed by Rudra Coresoft */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: '0.75rem', color: mutedColor }}>Developed by</span>
            <a
              href={RUDRA_URL}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'block', transition: 'opacity 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            >
              <RudraLogo color={accentColor} />
            </a>
          </div>

          <div style={{ fontSize: '0.7rem', color: mutedColor, textAlign: 'center' }}>
            © {new Date().getFullYear()} Rudra Coresoft Pvt Ltd. Bangalore, India. All Rights Reserved.
          </div>

        </div>
      </div>
    </footer>
  );
}
